import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '../ui/alert'
import { useToast } from '@/hooks/use-toast'
import { 
  Database, 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle, 
  Info,
  Shield,
  Globe,
  Copy,
  ExternalLink,
  Key,
  Lock
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'

interface WIFInstructions {
  principalIdentifier: string;
  gcloudCommand: string;
  consoleUrl: string;
  steps: string[];
}

interface WIFConnection {
  connectionName: string
  projectId: string
  instructions: WIFInstructions
  dataSourceId: string
}

interface FirestoreWIFSetupFormProps {
  onBack: () => void
  onComplete: (connection: WIFConnection) => void
  organizationId: string
}

const FirestoreWIFSetupForm: React.FC<FirestoreWIFSetupFormProps> = ({ onBack, onComplete, organizationId }) => {
  const [connectionName, setConnectionName] = useState('')
  const [projectId, setProjectId] = useState('')
  const [targetServiceAccountEmail, setTargetServiceAccountEmail] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [instructions, setInstructions] = useState<WIFInstructions | null>(null)
  const [dataSourceId, setDataSourceId] = useState<string | null>(null)
  const [isTestingConnection, setIsTestingConnection] = useState(false)
  const { toast } = useToast()
  const { currentUser } = useAuth()

  const handleCreateWIFDataSource = async () => {
    if (!connectionName.trim() || !projectId.trim() || !targetServiceAccountEmail.trim()) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in connection name, your Firebase Project ID, and your Target Service Account Email.",
      })
      return
    }
    if (!currentUser?.uid) {
      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: "User authentication details are not available. Please try logging in again.",
      })
      return
    }

    setIsCreating(true)
    
    try {
      const generateWIFDataSource = httpsCallable(functions, 'GenerateFirestoreWIFDataSource')

      const response = await generateWIFDataSource({
          name: connectionName,
          targetProjectId: projectId,
          targetServiceAccountEmail: targetServiceAccountEmail,
          organizationId: organizationId,
      })

      const result = response.data as { success: boolean; data?: { instructions: any; dataSourceId: string }; message?: string; error?: string }

      if (result.success && result.data?.instructions && result.data?.dataSourceId) {
        setInstructions(result.data.instructions)
        setDataSourceId(result.data.dataSourceId)
        toast({
          title: "WIF Configuration Generated",
          description: result.message || "Follow the instructions below to grant FireForge access to your Firestore project.",
        })
      } else {
        throw new Error(result.message || result.error || 'Failed to create WIF configuration')
      }

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Creation Failed",
        description: error.message || "Failed to create WIF configuration.",
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleTestConnection = async () => {
    if (!dataSourceId) return
    if (!currentUser) {
      toast({ variant: "destructive", title: "Authentication Error", description: "User not authenticated." })
      return
    }
    
    setIsTestingConnection(true)
    
    try {
      const testWIFConnection = httpsCallable(functions, 'testWIFConnection')

      const response = await testWIFConnection({
          dataSourceId: dataSourceId,
          type: 'firestore-wif'
      })

      const result = response.data as { success: boolean; message?: string; error?: string }

      if (result.success) {
        toast({
          title: "Connection Successful!",
          description: result.message || "Successfully connected to your Firestore instance.",
        })
        
        if (instructions && dataSourceId) {
          onComplete({
            connectionName,
            projectId,
            instructions,
            dataSourceId
          })
        }
      } else {
        toast({
          title: "Connection Test Failed",
          description: result.error || "The connection test failed. Please verify the IAM permissions are set correctly as per the instructions.",
          variant: "destructive",
        })
      }

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Test Failed",
        description: error.message || "Failed to test the connection.",
      })
    } finally {
      setIsTestingConnection(false)
    }
  }

  const copyToClipboard = (text: string, description: string) => {
    navigator.clipboard.writeText(text)
    toast({
      title: "Copied to Clipboard",
      description: `${description} copied to clipboard.`,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Data Sources
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold leading-tight text-foreground">
            Secure Firestore Connection (via WIF)
          </h1>
          <p className="text-sm text-muted-foreground">
            Connect to your Firestore using Workload Identity Federation - no service account keys required.
          </p>
        </div>
      </div>

      {/* Security Benefits */}
      <Alert>
        <Lock className="h-4 w-4" />
        <AlertDescription>
          <strong>Enhanced Security:</strong> Workload Identity Federation eliminates the need to manage 
          service account keys. FireForge will access your Firestore using short-lived, automatically 
          rotated tokens with minimal required permissions.
        </AlertDescription>
      </Alert>

      {/* Step 1: Basic Information */}
      {!instructions && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Step 1: Provide Your Connection Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="connection-name">Connection Name *</Label>
              <Input
                id="connection-name"
                value={connectionName}
                onChange={(e) => setConnectionName(e.target.value)}
                placeholder="e.g., My App\'s User Data"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                A friendly name for this data source in FireForge.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-id">Your Target Firebase Project ID *</Label>
              <Input
                id="project-id"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="your-target-firebase-project-id"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                The Project ID of the Google Cloud Project where your Firestore database is located.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target-sa-email">Your Target Service Account Email *</Label>
              <Input
                id="target-sa-email"
                value={targetServiceAccountEmail}
                onChange={(e) => setTargetServiceAccountEmail(e.target.value)}
                placeholder="your-sa-name@your-target-project-id.iam.gserviceaccount.com"
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                The email of a service account in your target project. This service account must have Firestore access (e.g., \'Cloud Datastore User\' role). FireForge will impersonate this service account.
              </p>
            </div>

            <Button 
              onClick={handleCreateWIFDataSource}
              disabled={isCreating || !connectionName.trim() || !projectId.trim() || !targetServiceAccountEmail.trim()}
              className="w-full"
            >
              {isCreating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent mr-2" />
                  Generating Secure Configuration...
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4 mr-2" />
                  Generate Setup Instructions
                </>
              )}
            </Button>

            {/* Test Button for the WIF function */}
            <Button 
              variant="outline"
              className="w-full mt-2"
              onClick={async () => {
                try {
                  const generateWIFDataSource = httpsCallable(functions, 'GenerateFirestoreWIFDataSource');
                  const result = await generateWIFDataSource({
                    name: "Test Connection",
                    targetProjectId: "test-project-id",
                    targetServiceAccountEmail: "test@test-project.iam.gserviceaccount.com",
                    organizationId: "test-org-id"
                  });
                  console.log('GenerateFirestoreWIFDataSource result:', result.data);
                  const data = result.data as { success: boolean; message?: string; error?: string };
                  if (data.success) {
                    toast({ 
                      title: 'GenerateFirestoreWIFDataSource Success!', 
                      description: data.message || 'WIF function works!',
                    });
                  } else {
                    toast({ 
                      title: 'GenerateFirestoreWIFDataSource Failed', 
                      description: data.error || 'Unknown error', 
                      variant: 'destructive' 
                    });
                  }
                } catch (error: any) {
                  console.error('GenerateFirestoreWIFDataSource error:', error);
                  toast({ 
                    title: 'GenerateFirestoreWIFDataSource Error', 
                    description: error.message, 
                    variant: 'destructive' 
                  });
                }
              }}
            >
              🔥 Test WIF Generation Function
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Setup Instructions */}
      {instructions && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Step 2: Grant FireForge Access in Your Google Cloud Project
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="default">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You need to perform these steps in <strong>your Google Cloud Project: {projectId}</strong>.
                  The service account you\'ll grant access to is: <strong>{targetServiceAccountEmail}</strong>.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>FireForge Principal Identifier (to grant access to)</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    value={instructions.principalIdentifier} 
                    readOnly 
                    className="font-mono text-xs bg-muted"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(instructions.principalIdentifier, "Principal identifier")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This unique identifier represents your FireForge account for WIF.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Quick Access to Your Project\'s IAM Page</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => window.open(instructions.consoleUrl, '_blank')}
                    disabled={!instructions.consoleUrl}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open IAM Page for Project: {projectId}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(instructions.consoleUrl, "IAM console URL")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Setup Steps to Perform in Your GCP Project</Label>
                <div className="space-y-2">
                  {instructions.steps.map((step, index) => (
                    <div key={index} className="flex items-start gap-3 text-sm">
                      <Badge variant="outline" className="mt-0.5 text-xs shrink-0">
                        {step.match(/^\\d+\\. /) ? step.substring(0, step.indexOf('.') +1) : index + 1}
                      </Badge>
                      <span className="text-muted-foreground">{step.replace(/^\\d+\\. /, '')}</span>
                    </div>
                  ))}
                </div>
              </div>

              <details className="space-y-2">
                <summary className="text-sm font-medium cursor-pointer">
                  Alternative: Use gcloud Command (Advanced)
                </summary>
                <div className="bg-muted p-3 rounded-md">
                  <p className="text-xs text-muted-foreground mb-2">Run this command in your Google Cloud Shell or a terminal with gcloud configured for project <strong>{projectId}</strong>:</p>
                  <pre className="text-xs overflow-x-auto bg-background p-2 rounded">{instructions.gcloudCommand}</pre>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => copyToClipboard(instructions.gcloudCommand, "gcloud command")}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Command
                  </Button>
                </div>
              </details>
            </CardContent>
          </Card>

          {/* Step 3: Test Connection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Step 3: Test Your Secure Connection
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                After completing the steps in your Google Cloud Project to grant access, click below to test the connection.
              </p>
              
              <Button 
                onClick={handleTestConnection}
                disabled={isTestingConnection || !dataSourceId}
                className="w-full"
              >
                {isTestingConnection ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent mr-2" />
                    Testing Connection...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Test Connection & Finalize
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* Security Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Benefits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <strong>No Long-lived Credentials:</strong> No service account keys to manage, rotate, or accidentally expose.
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Minimal Permissions:</strong> Only the permissions you explicitly grant are used.
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Automatic Token Rotation:</strong> Short-lived tokens are automatically refreshed.
            </div>
          </div>
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <strong>Audit Trail:</strong> All access is logged in Google Cloud's audit logs.
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default FirestoreWIFSetupForm 